import { CloseOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EntityMeta, FieldMeta, GroupMeta, Workflow } from "@wms/domain";
import { Alert, App, Button, Col, Collapse, ConfigProvider, DatePicker, Descriptions, Form, Grid, Input, InputNumber, Popconfirm, Radio, Row, Select, Space, Steps, Switch, Typography } from "antd";
import type { FormInstance } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api, describeError, fieldErrors } from "../api/client";
import type { Page } from "../api/client";
import { formatDate, formatDateTime } from "../api/format";
import { useAuthStore } from "../auth/store";
import { useUnsaved } from "../state/unsaved";
import { ActionPanel } from "./ActionPanel";
import { AttributesField } from "./AttributesField";
import { AssistantDocumentButton } from "./AssistantDocumentButton";
import { DocumentActions } from "./DocumentActions";
import { GuardedInput } from "./GuardedInput";
import { FormPreview } from "./FormPreview";
import { columnSpan, formLayout, tooLong, useElementWidth } from "./formLayout";
import type { FormProblem } from "./formProblem";
import { checkWords, explainProblem, labelOf } from "./formProblem";
import { HelpHint } from "./HelpHint";
import { policyOfField, unfinishedNumber, withTaxLabel } from "./inputPolicy";
import { checkProblem, helpFor, labelFor, switchNotice, visibleOptions } from "./kzRequisites";
import { PermissionsSelect } from "./PermissionsSelect";
import { activeSource, choosePrompt, dependentFilter, fillWords, ruleOf } from "./dependentRef";
import { allowedFields, fixFor, onlyChangedAllowed } from "./editPolicy";
import { refreshChildLists } from "./recordQueries";
import { EntitySelect, RefLabel, RefSelect, rowLabel, useRefRows, useServerLabel } from "./RefSelect";
import { MAIN_PART, formParts } from "./stepwise";

export type Row = Record<string, unknown> & { id: string };

function editableFields(meta: EntityMeta, isNew: boolean): FieldMeta[] {
  return meta.fields.filter((f) => !f.hidden && !f.readonly && (isNew || f.type !== "password"));
}

/** Map API object to form values: role objects -> role codes, ISO dates -> dayjs, JSON -> text. */
export function toFormValues(meta: EntityMeta, row: Row): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const f of meta.fields) {
    let value = row[f.name];
    if (value === undefined && f.type === "multiref" && f.name.endsWith("_codes")) {
      const related = row[f.name.replace(/_codes$/, "s")];
      if (Array.isArray(related)) value = related.map((r: Record<string, unknown>) => r.code);
    }
    if (f.type === "json" && !RAW_JSON_FIELDS.has(f.name) && value !== undefined && value !== null) value = JSON.stringify(value, null, 2);
    if ((f.type === "datetime" || f.type === "date") && value) value = dayjs(String(value));
    if (f.type === "number" && value !== undefined && value !== null) value = Number(value);
    values[f.name] = value ?? undefined;
  }
  return values;
}

/**
 * Form values → request body. BUG-R31-008: a field the user cleared (a reference, an enum, a date) comes back from
 * the control as `undefined`; when the saved record had a value there, the clearing is sent as `null` — otherwise the
 * field was simply left out, the server kept the old state and the form showed an empty field that was not saved.
 * A field the form never had a value for is still omitted, so nothing unseen is wiped by the side (COMP-07: a field
 * hidden by the layout is not in `values` and is never sent as null).
 */
export function fromFormValues(meta: EntityMeta, values: Record<string, unknown>, saved?: Row | null): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const f of meta.fields) {
    if (!(f.name in values)) continue;
    let value = values[f.name];
    if (value === undefined || value === null) {
      const before = saved ? saved[f.name] : undefined;
      if (saved && before !== undefined && before !== null && before !== "" && f.type !== "password") body[f.name] = null;
      continue;
    }
    if (f.type === "json" && !RAW_JSON_FIELDS.has(f.name)) value = value === "" || value === null ? null : JSON.parse(String(value));
    if (f.type === "datetime" && value) value = (value as dayjs.Dayjs).toISOString();
    if (f.type === "date" && value) value = (value as dayjs.Dayjs).format("YYYY-MM-DD");
    if (f.type === "password" && !value) continue;
    body[f.name] = value === "" ? null : value;
  }
  return body;
}

/** JSON fields edited by a dedicated control rather than as text. */
const RAW_JSON_FIELDS = new Set(["attributes", "permissions"]);

/** «2026-10-29» in the warehouse's time zone — the date default of a new document (section 6.2, AT-03). */
export function todayIn(timeZone: string | undefined): { date: string; zone: string | null } {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
      if (/^\d{4}-\d{2}-\d{2}$/.test(parts)) return { date: parts, zone: timeZone };
    } catch {
      /* unknown zone name: fall back to the device */
    }
  }
  return { date: dayjs().format("YYYY-MM-DD"), zone: null };
}

/**
 * Form.Item injects value/onChange/id into its child: every control below must receive them (`rest`).
 * `guided` (UX3010-04): a comment and other full-row texts are multi-line with word wrap; a long value is never cut
 * by the browser (`maxLength` drops the tail of a paste silently) — the limit is a rule that says how much is too much,
 * and the whole value is readable on hover.
 */
function FieldInput({ field, depth, onCreated, guided, saved, guard, ...rest }: { field: FieldMeta; depth?: number; onCreated?: (row: Record<string, unknown>) => void; guided?: boolean; saved?: unknown; guard?: boolean } & Record<string, unknown>) {
  // UXW-60…62 (option «Запрет недопустимого ввода»): a number, a requisite, a phone or a text with a limit judges every
  // edit by the policy of its meta; codes, comments and every other control stay as they are
  const policy = guard ? policyOfField(field) : null;
  if (policy && policy.kind !== "free" && (field.type === "number" || field.type === "string")) {
    return <GuardedInput policy={policy} style={field.type === "number" ? { width: "100%" } : undefined} placeholder={field.placeholder ?? undefined} {...rest} />;
  }
  switch (field.type) {
    case "boolean":
      return <Switch {...rest} />;
    case "number":
      // no silent clamping: a wrong value stays visible and the rule below explains it (B07, section 7)
      return <InputNumber style={{ width: "100%" }} decimalSeparator="," placeholder={field.placeholder ?? undefined} {...rest} />;
    case "datetime":
      return <DatePicker showTime style={{ width: "100%" }} format="DD.MM.YYYY HH:mm" {...rest} />;
    case "date":
      return <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" {...rest} />;
    case "ref":
      return <RefSelect entity={field.ref!} staticFilter={field.filter ?? null} placeholder={field.placeholder ?? undefined} create={field.create} depth={depth} onCreated={onCreated} {...rest} />;
    case "multiref":
      return <RefSelect entity={field.ref!} valueField={field.name.endsWith("_codes") ? "code" : "id"} multiple {...rest} />;
    case "refcode":
      return <RefSelect entity={field.ref!} valueField="code" {...rest} />;
    case "entity":
      return <EntitySelect {...rest} />;
    case "enum":
      // KZ 30.10.2026 (R02): a historical option is offered only to the record that already has it
      if (field.widget === "radio") {
        return <Radio.Group optionType="button" buttonStyle="solid" options={visibleOptions(field.options, saved).map((o) => ({ value: o.value, label: o.label }))} data-testid={`radio-${field.name}`} {...rest} />;
      }
      return <Select allowClear showSearch={(field.options?.length ?? 0) > 12} optionFilterProp="label" options={visibleOptions(field.options, saved)} {...rest} />;
    case "multienum":
      return <Select mode="multiple" allowClear options={field.options ?? []} {...rest} />;
    case "password":
      return <Input.Password autoComplete="new-password" {...rest} />;
    case "json":
      return <Input.TextArea rows={4} style={{ fontFamily: "monospace" }} {...rest} />;
    default:
      if (guided && (field.name === "comment" || field.width === "xl")) {
        return <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} placeholder={field.placeholder ?? undefined} {...rest} />;
      }
      if (guided) {
        const text = typeof rest.value === "string" ? rest.value : "";
        return <Input placeholder={field.placeholder ?? undefined} title={text.length > 16 ? text : undefined} {...rest} />;
      }
      if ((field.max_length ?? 0) > 300) return <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} placeholder={field.placeholder ?? undefined} {...rest} />;
      // UXW-62: with the input guard a long code is never cut by the browser — the rule under the field says so
      if (guard) return <Input placeholder={field.placeholder ?? undefined} {...rest} />;
      // section 5.2: no permanent «0 / 64» under an ordinary short field — the limit stays, the counter shows near it
      return <Input maxLength={field.max_length ?? undefined} showCount={(field.max_length ?? 0) > 0 && (field.max_length ?? 0) <= 32 && !field.width} placeholder={field.placeholder ?? undefined} {...rest} />;
  }
}

/**
 * A reference whose list follows another field of the same form (WMS-03): aisles of the chosen room, addresses of the
 * chosen partner. Never disabled without a reason: when the parent is empty the placeholder says what to pick first.
 * «+» passes the chosen parent to the new row (section 7.3); without a parent there is nothing to create under.
 * CW31-B06: a field with `depends_rules` (the contract of the owner of the goods) is drawn by RuleDependentRefSelect.
 */
function DependentRefSelect(props: { field: FieldMeta; form: FormInstance; labels: Record<string, string>; refs: Record<string, string | null | undefined>; depth?: number; onCreated?: (row: Record<string, unknown>) => void } & Record<string, unknown>) {
  if (props.field.depends_rules) return <RuleDependentRefSelect {...props} />;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refs, ...rest } = props;
  return <PlainDependentRefSelect {...rest} />;
}

function PlainDependentRefSelect({ field, form, labels, depth, onCreated, ...rest }: { field: FieldMeta; form: FormInstance; labels: Record<string, string>; depth?: number; onCreated?: (row: Record<string, unknown>) => void } & Record<string, unknown>) {
  const dep = field.depends_on![0];
  const depValue = Form.useWatch(dep, form) as string | undefined;
  const previous = useRef<string | undefined>(undefined);
  useEffect(() => {
    // the parent changed after the form was filled: the old child value no longer belongs to the list
    if (previous.current !== undefined && previous.current !== depValue && form.getFieldValue(field.name)) {
      form.setFieldValue(field.name, undefined);
    }
    previous.current = depValue ?? undefined;
  }, [depValue, field.name, form]);
  const depLabel = labels[dep] ?? dep;
  return (
    <RefSelect
      entity={field.ref!}
      filter={depValue ? { field: dep, op: "eq", value: depValue } : null}
      staticFilter={field.filter ?? null}
      placeholder={depValue ? (field.placeholder ?? undefined) : `Сначала выберите «${depLabel}»`}
      emptyHint={depValue ? `Для выбранного значения «${depLabel}» записей нет${field.create ? " — создайте через «+» ниже" : ` — создайте их в разделе «${field.label}»`}` : `Сначала выберите «${depLabel}»`}
      create={field.create && !!depValue}
      createParams={depValue ? { [dep]: depValue } : undefined}
      createParentWord={`значению «${depLabel}»`}
      depth={depth}
      onCreated={onCreated}
      {...rest}
    />
  );
}

/**
 * CW31-B06 / B22: a reference filtered by the first filled of several fields under the column the meta names — the
 * contracts of the owner of the goods (`contracts.partner_id = owner_id`), else of the recipient; the lines of the
 * order of the chosen cargo unit (`via`: the `order_id` of that unit). A changed recipient does not touch the contract
 * of the owner; a changed owner checks the chosen contract again and says why it was cleared.
 */
function RuleDependentRefSelect({ field, form, labels, refs, depth, onCreated, ...rest }: { field: FieldMeta; form: FormInstance; labels: Record<string, string>; refs: Record<string, string | null | undefined>; depth?: number; onCreated?: (row: Record<string, unknown>) => void } & Record<string, unknown>) {
  const deps = field.depends_on ?? [];
  const values = Form.useWatch((all: Record<string, unknown>) => Object.fromEntries(deps.map((d) => [d, all?.[d] ?? null])), { form, preserve: true }) as Record<string, unknown> | undefined;
  const active = activeSource(field, values ?? {});
  const rule = active ? ruleOf(field, active.source) : {};
  const sourceRef = active ? refs[active.source] ?? undefined : undefined;
  // `via`: the row chosen in the source names the value that filters (the order of a cargo unit) — the same query and
  // cache as the card of that row
  const viaRow = useQuery({
    queryKey: [sourceRef, active?.value],
    queryFn: () => api.get<Row>(`/${sourceRef}/${active!.value}`),
    enabled: !!rule.via && !!sourceRef && !!active,
    staleTime: 60_000,
  });
  const viaPending = !!rule.via && !!active && !!sourceRef && !viaRow.data && !viaRow.isError;
  const filterValue = !active ? undefined : rule.via ? (viaRow.data?.[rule.via] as string | undefined) ?? undefined : active.value;
  const sourceName = useServerLabel(sourceRef, active?.value, !!active && !!sourceRef);
  const [notice, setNotice] = useState<string | null>(null);
  const previous = useRef<string | undefined>(undefined);
  useEffect(() => {
    const before = previous.current;
    previous.current = filterValue;
    if (before === undefined || filterValue === undefined || before === filterValue) return;
    const current = form.getFieldValue(field.name) as string | undefined;
    if (!current) return;
    // the source changed after the form was filled: a value that still fits stays, one that does not is cleared
    // with the reason — never silently
    let cancelled = false;
    const target = ruleOf(field, active!.source).target ?? active!.source;
    const clear = () => {
      if (cancelled) return;
      form.setFieldValue(field.name, undefined);
      setNotice(ruleOf(field, active!.source).reset ?? "Значение очищено: оно не подходит к новому выбору — выберите заново");
    };
    api
      .get<Row>(`/${field.ref}/${current}`)
      .then((chosen) => (String(chosen[target] ?? "") === filterValue ? undefined : clear()))
      .catch(clear);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);
  useEffect(() => {
    if (rest.value) setNotice(null);
  }, [rest.value]);
  const name = sourceName.data ?? undefined;
  const prompt = choosePrompt(field, labels);
  const plus = field.create ? " — создайте через «+» ниже" : "";
  const emptyHint = !active
    ? prompt
    : viaPending
      ? "Загружаем…"
      : rule.empty
        ? `${fillWords(rule.empty, name)}${plus}`
        : `Для выбранного значения «${labels[active.source] ?? active.source}» записей нет${plus || ` — создайте их в разделе «${field.label}»`}`;
  const target = active ? dependentFilter(field, active.source, filterValue ?? "").field : undefined;
  return (
    <>
      <RefSelect
        entity={field.ref!}
        filter={active && filterValue ? dependentFilter(field, active.source, filterValue) : null}
        staticFilter={field.filter ?? null}
        disabled={viaPending || undefined}
        placeholder={active ? (rule.placeholder ?? field.placeholder ?? undefined) : prompt}
        emptyHint={emptyHint}
        create={field.create && !!filterValue}
        createParams={target && filterValue ? { [target]: filterValue } : undefined}
        createParentWord={active ? `значению «${labels[active.source] ?? active.source}»` : undefined}
        depth={depth}
        onCreated={onCreated}
        {...rest}
      />
      {notice && (
        <Typography.Text type="warning" style={{ display: "block", marginTop: 4 }} data-testid={`depends-reset-${field.name}`}>
          {fillWords(notice, name)}
        </Typography.Text>
      )}
    </>
  );
}

/** A reference whose list comes from another field's value: the dock slot's document is a receipt or an order. */
function RefByFieldSelect({ field, form, labels, ...rest }: { field: FieldMeta; form: FormInstance; labels: Record<string, string> } & Record<string, unknown>) {
  const spec = field.ref_by!;
  const selector = Form.useWatch(spec.field, form) as string | undefined;
  const entity = selector ? spec.map[selector] : undefined;
  if (!entity) {
    return <RefSelect entity={Object.values(spec.map)[0]} disabled placeholder={`Сначала выберите «${labels[spec.field] ?? spec.field}»`} {...rest} />;
  }
  return <RefSelect entity={entity} placeholder="Начните вводить номер документа" {...rest} />;
}

/**
 * UI-11: «Доставка» / «Самовывоз» over the existing delivery types — the two modes are the flag `is_pickup` of the
 * rows, never their names. A mode with one type picks it; with several, the list below is narrowed to the group.
 * An unknown value is never forced into a mode (section 10.2).
 */
function ModeRefSelect({ field, value, onChange, disabled, ...rest }: { field: FieldMeta; value?: string; onChange?: (v: string | undefined) => void; disabled?: boolean } & Record<string, unknown>) {
  const mode = field.mode!;
  const rows = useRefRows(field.ref!);
  const items = rows.data?.items ?? [];
  const current = value ? items.find((r) => String(r.id) === value) : undefined;
  const [chosen, setChosen] = useState<boolean | undefined>(undefined);
  const active = current ? Boolean(current[mode.field]) : chosen;
  const pickMode = (flag: boolean) => {
    setChosen(flag);
    const group = items.filter((r) => Boolean(r[mode.field]) === flag);
    if (current && Boolean(current[mode.field]) !== flag) onChange?.(group.length === 1 ? String(group[0].id) : undefined);
    else if (!current && group.length === 1) onChange?.(String(group[0].id));
  };
  return (
    <Space direction="vertical" style={{ width: "100%" }} size={6}>
      <Radio.Group
        optionType="button"
        buttonStyle="solid"
        disabled={disabled}
        value={active}
        onChange={(e) => pickMode(Boolean(e.target.value))}
        options={mode.options.map((o) => ({ value: o.value, label: o.label }))}
        data-testid={`mode-${field.name}`}
      />
      <RefSelect
        entity={field.ref!}
        value={value}
        onChange={(v) => onChange?.(typeof v === "string" ? v : undefined)}
        disabled={disabled}
        filter={active === undefined ? null : { field: mode.field, op: "eq", value: active }}
        placeholder={active === undefined ? "Выберите режим или вид доставки" : "Уточните вид доставки"}
        emptyHint="Для этого режима виды доставки не настроены: добавьте их в справочнике «Виды доставки»"
        create={field.create}
        {...rest}
      />
    </Space>
  );
}

/**
 * N06: «Упаковок» × коэффициент выбранной упаковки сразу виден в поле «План» — сотрудник не вводит число,
 * которое система и так посчитает. Сервер повторяет тот же расчёт при сохранении.
 */
function usePackagePlan(form: FormInstance, enabled: boolean): { planHint?: string; packageHint?: string } {
  const packageId = Form.useWatch("package_id", form) as string | undefined;
  const unit = useProductUnit(form, enabled ? "storage_object_id" : null);
  const count = Form.useWatch("packages_count", form) as number | string | null | undefined;
  const pkg = useQuery({
    queryKey: ["packages", packageId],
    queryFn: () => api.get<Record<string, unknown>>(`/packages/${packageId}`),
    enabled: enabled && !!packageId,
    staleTime: 60_000,
  });
  const ratio = pkg.data?.ratio !== undefined && pkg.data?.ratio !== null ? Number(pkg.data.ratio) : undefined;
  const n = count === undefined || count === null || count === "" ? undefined : Number(count);
  const computed = enabled && ratio && n && n > 0 ? Math.round(n * ratio * 1000) / 1000 : undefined;
  useEffect(() => {
    if (computed !== undefined) form.setFields([{ name: "quantity_plan", value: computed, errors: [] }]);
  }, [computed, form]);
  if (!enabled || !packageId) return {};
  // section 11.4: «1 короб = 50 шт» next to the package, «3 × 50 = 150 шт» next to the quantity
  const packageHint = ratio ? `1 ${String(pkg.data?.name ?? "упаковка").toLowerCase()} = ${ratio} ${unit ?? "базовых ед."}` : undefined;
  if (computed !== undefined) return { planHint: `Рассчитано автоматически: ${n} × ${ratio} = ${computed} ${unit ?? "базовых единиц"}`, packageHint };
  return { planHint: "Укажите «Количество упаковок» — количество посчитается по коэффициенту упаковки", packageHint };
}

/** The unit of the product chosen in the form («кг», «шт», «л»): read from the product card, so a weight product never
 * gets «в штуках» and a change of the product updates the hint at once (BUG-R40-004). */
export function useProductUnit(form: FormInstance, objectField: string | null | undefined): string | undefined {
  const objectId = Form.useWatch(objectField ?? "__none__", form) as string | undefined;
  const obj = useQuery({
    queryKey: ["storage_objects", objectId],
    queryFn: () => api.get<Record<string, unknown>>(`/storage_objects/${objectId}`),
    enabled: !!objectField && !!objectId,
    staleTime: 60_000,
  });
  const unitId = obj.data?.unit_id as string | undefined;
  const unit = useQuery({
    queryKey: ["units", unitId],
    queryFn: () => api.get<Record<string, unknown>>(`/units/${unitId}`),
    enabled: !!unitId,
    staleTime: 300_000,
  });
  return unit.data ? String(unit.data.name ?? unit.data.code ?? "") || undefined : undefined;
}

/** The unit of the chosen product with its precision («кг», 3 digits) — the same queries and cache as the hint. */
function useProductUnitInfo(form: FormInstance, objectField: string | null | undefined): { name: string; decimals: number } | null {
  const objectId = Form.useWatch(objectField ?? "__none__", form) as string | undefined;
  const obj = useQuery({
    queryKey: ["storage_objects", objectId],
    queryFn: () => api.get<Record<string, unknown>>(`/storage_objects/${objectId}`),
    enabled: !!objectField && !!objectId,
    staleTime: 60_000,
  });
  const unitId = obj.data?.unit_id as string | undefined;
  const unit = useQuery({
    queryKey: ["units", unitId],
    queryFn: () => api.get<Record<string, unknown>>(`/units/${unitId}`),
    enabled: !!unitId,
    staleTime: 300_000,
  });
  if (!unit.data) return null;
  return { name: String(unit.data.name ?? unit.data.code ?? ""), decimals: Number(unit.data.decimals ?? 0) };
}

/** IN06: a quantity in the unit of the chosen product — 1,5 шт is refused with the reason, 1,25 кг is taken. */
function UnitNumberInput({ field, form, ...rest }: { field: FieldMeta; form: FormInstance } & Record<string, unknown>) {
  const unit = useProductUnitInfo(form, field.unit_from);
  return <GuardedInput policy={policyOfField(field, unit)!} style={{ width: "100%" }} placeholder={field.placeholder ?? undefined} {...rest} />;
}

/** IN01–IN03: «БИН» for a ТОО, «ИИН» for an ИП — the reason names the identifier of the chosen form. */
function TaxIdInput({ field, form, ...rest }: { field: FieldMeta; form: FormInstance } & Record<string, unknown>) {
  const kind = Form.useWatch(field.label_by?.field ?? "__none__", { form, preserve: true });
  return <GuardedInput policy={withTaxLabel(policyOfField(field), kind)!} placeholder={field.placeholder ?? undefined} {...rest} />;
}

function UnitHint({ form, field }: { form: FormInstance; field: FieldMeta }) {
  const unit = useProductUnit(form, field.unit_from);
  if (!unit) return <>{field.description ?? "В базовой единице товара: выберите товар, и здесь появится его единица"}</>;
  return <>{`В базовой единице товара: ${unit}. Если заполнить «Количество упаковок», количество пересчитается само.`}</>;
}

/**
 * «Создано … ; изменено …» only from dates the record really has (BUG-TSD-003 / Z-010 / H-006): a journal row keeps
 * its own «Время» field and gets no footer, an immutable record shows «создано» alone, nothing is ever «Invalid Date».
 */
function auditLine(row: Row): string {
  const created = formatDateTime(row.created_at);
  const updated = formatDateTime(row.updated_at);
  const parts: string[] = [];
  if (created) parts.push(`Создано ${created} ${String(row.created_by ?? "")}`.trim());
  if (updated) parts.push(`изменено ${updated} ${String(row.updated_by ?? "")}`.trim());
  return parts.join("; ");
}

/** Number rules from the meta: shown next to the field instead of changing the typed value. */
function numberRules(f: FieldMeta) {
  if (f.type !== "number" || f.min === null || f.min === undefined) return [];
  const min = f.min;
  const exclusive = !!f.min_exclusive;
  return [
    {
      validator: (_: unknown, value: unknown) => {
        if (value === undefined || value === null || value === "") return Promise.resolve();
        const n = Number(value);
        if (Number.isNaN(n)) return Promise.reject(new Error("Введите число"));
        if (exclusive ? n <= min : n < min) return Promise.reject(new Error(exclusive ? `Введите число больше ${min}` : `Введите число не меньше ${min}`));
        return Promise.resolve();
      },
    },
  ];
}

/** A collapsed group («Допуски количества») opens itself when a field inside has a value, an error or is required. */
function CollapsedGroup({ group, fields, form, children }: { group: GroupMeta; fields: FieldMeta[]; form: FormInstance; children: React.ReactNode }) {
  const filled = Form.useWatch((values: Record<string, unknown>) => fields.some((f) => values?.[f.name] !== undefined && values?.[f.name] !== null && values?.[f.name] !== ""), form);
  const errors = fields.some((f) => form.getFieldError(f.name).length > 0);
  const [opened, setOpened] = useState(false);
  const open = opened || !!filled || errors || fields.some((f) => f.required);
  return (
    <Collapse
      ghost
      activeKey={open ? [group.key] : []}
      onChange={(keys) => setOpened((Array.isArray(keys) ? keys : [keys]).includes(group.key))}
      style={{ marginBottom: 12 }}
      items={[{ key: group.key, forceRender: true, label: <span>{group.label}{group.help && <HelpHint subject={group.label} text={group.help} size={13} />}</span>, children }]}
    />
  );
}

/** KZ 30.10.2026 (ТЗ реквизитов §5.1): «БИН» while «ТОО» is chosen, «ИИН» for «ИП» — the label follows the form. */
function DynamicLabel({ field, form }: { field: FieldMeta; form: FormInstance }) {
  const by = Form.useWatch(field.label_by?.field ?? "__none__", { form, preserve: true });
  const label = labelFor(field, by);
  const help = helpFor(field, by);
  return (
    <span data-testid={`label-${field.name}`}>
      {label}
      {help && <HelpHint subject={label} text={help} size={13} field />}
    </span>
  );
}

/**
 * §5.2: after a change of the form while the identifier is filled — «Проверьте идентификатор: теперь нужен ИИН личного
 * ИП». The identifier is never rewritten; typing it again for the new form removes the warning.
 */
function SwitchNotice({ field, form, saved }: { field: FieldMeta; form: FormInstance; saved: unknown }) {
  const target = field.notice_on_change!.field;
  const now = Form.useWatch(field.name, { form, preserve: true });
  const value = Form.useWatch(target, { form, preserve: true });
  const typedFor = useRef<unknown>(saved);
  const lastValue = useRef<unknown>(value);
  useEffect(() => {
    // the identifier typed (or loaded) under the current form belongs to it
    if (value !== lastValue.current) {
      lastValue.current = value;
      typedFor.current = now;
    }
  }, [value, now]);
  useEffect(() => {
    if (typedFor.current === undefined && now !== undefined) typedFor.current = now;
  }, [now]);
  const notice = switchNotice(field, typedFor.current, now, value);
  return notice ? <Alert type="warning" showIcon style={{ marginTop: 6 }} message={notice} data-testid={`notice-${field.name}`} /> : null;
}

/** «Организация: ТОО Ромашка (единственная, подставлена автоматически) — изменить можно в «Дополнительно»». */
function ContextItem({ field, form, candidate }: { field: FieldMeta; form: FormInstance; candidate: Record<string, unknown> }) {
  const value = Form.useWatch(field.name, { form, preserve: true }) as string | undefined;
  return (
    <span data-testid={`context-${field.name}`}>
      {" · "}
      {field.label}: {value ? <RefLabel entity={field.ref!} id={value} /> : "не указана"}
      {value && String(candidate.id) === value ? " (единственная, подставлена автоматически)" : ""}
      {" — изменить можно в «Дополнительно»"}
    </span>
  );
}

/**
 * Section 12.1: the stages of the document and the one recommended step, from GET /{entity}/{id}/workflow. With the
 * grouped action panel (`hideNext`) the recommendation and its button are said once — in the panel below the strip.
 */
function WorkflowPanel({ workflow, onOpenTab, hideNext }: { workflow: Workflow; onOpenTab?: (key: string) => void; hideNext?: boolean }) {
  const next = workflow.recommended;
  const current = workflow.stages.findIndex((s) => s.state === "current");
  // KZ 30.10.2026: on a phone the button of the next step goes under the text — beside it, it took the whole width and
  // squeezed the text into one letter per line
  const narrow = !Grid.useBreakpoint().sm;
  const openTab =
    next && next.kind === "tab" && onOpenTab ? (
      <Button
        size="small"
        type="primary"
        data-testid="workflow-open-tab"
        onClick={() => onOpenTab(next.key!)}
        style={narrow ? { maxWidth: "100%", whiteSpace: "normal", height: "auto" } : undefined}
      >
        {next.label}
      </Button>
    ) : undefined;
  return (
    <div data-testid="workflow-panel" style={{ marginBottom: 16 }}>
      <Steps
        size="small"
        responsive
        current={current >= 0 ? current : workflow.stages.length}
        items={workflow.stages.map((s) => ({
          title: s.label,
          description: s.hint ?? (s.state === "skipped" ? "не требуется" : undefined),
          // ТЗ «Понятные шаги» UX1-006 (3): «waiting» — the stage of another role is shown in progress, with whose it is
          status: s.state === "done" ? "finish" : s.state === "current" || s.state === "waiting" ? "process" : "wait",
          className: s.state === "waiting" ? "ant-steps-item-waiting" : undefined,
        }))}
        style={{ marginBottom: 12 }}
      />
      {next && !hideNext && (
        <Alert
          data-testid="workflow-next"
          type={next.kind === "wait" ? "warning" : next.kind === "done" ? "success" : "info"}
          showIcon
          message={next.kind === "action" ? `Следующий шаг: «${next.label}»` : next.kind === "tab" ? `Следующий шаг: ${next.label.toLowerCase()}` : next.label}
          description={
            narrow && openTab ? (
              <>
                {next.instruction}
                <div style={{ marginTop: 8 }}>{openTab}</div>
              </>
            ) : (
              next.instruction
            )
          }
          action={narrow ? undefined : openTab}
        />
      )}
    </div>
  );
}

interface Props {
  entity: string;
  /** "new" or the id of the record. */
  id: string;
  /** Values the new record starts with (the parent of a child tab, the search text of «+»). */
  prefill?: Record<string, string>;
  /** Inside a drawer: no delete, a «Отмена» button, the saved row goes to onSaved instead of the router. */
  embedded?: boolean;
  /** How many create drawers are open above this form («+» inside is refused past the second level). */
  depth?: number;
  onSaved?: (row: Row) => void;
  onCancel?: () => void;
  /** The page switches to a tab the workflow recommends («Товары», «Задачи»). */
  onOpenTab?: (key: string) => void;
  /** The page shows «не сохранено» next to the title. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * ТЗ «Понятные шаги» (30.10.2026): the card is shown by steps. Only `part` of the input is visible (null — the whole
   * header); the strip and the document actions are drawn by the page; «Сохранить» is the main button only on the
   * last part. Every field stays mounted in the one Form store (UX1-011): a hidden part keeps its values and is
   * still validated — an error in it asks the page to open that part (UX1-025).
   */
  stepwise?: { part: string | null; last: boolean; onPartNeeded: (part: string) => void };
}

/**
 * The meta-driven card of any entity: one component for the page, the drawer behind «+» and every role. The guided
 * layout (groups, widths, context, source data, delivery modes, next step) is switched by the feature option
 * «Понятные формы и следующий шаг»; with it off the form looks and works as before.
 */
export function EntityForm({ entity, id, prefill = {}, embedded, depth = 0, onSaved, onCancel, onOpenTab, onDirtyChange, stepwise }: Props) {
  const isNew = id === "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, notification, modal } = App.useApp();
  const can = useAuthStore((s) => s.can);
  const option = useAuthStore((s) => s.option);
  const guided = option("use_guided_forms");
  // ТЗ «Рабочие столы» (31.10.2026): the grouped action panel (UXW-47…53) and the input guard (UXW-60…66) are rollout
  // options; off — the card of HEAD
  const panelOn = option("use_action_panel");
  const guardOn = option("use_input_guard");
  // UXW-57 / ER01: «Проверьте N полей» with a link to each — fields of the last refused save (input guard on)
  const [issues, setIssues] = useState<{ field: string; message: string }[]>([]);
  // UX3010-11 / 13: our organization of the session — the static line of a new receipt, the start of a new contract
  const company = useAuthStore((s) => s.company);
  const me = useAuthStore((s) => s.me);
  const workingCompany = company ?? me?.companies?.find((c) => c.id === me?.company_id) ?? null;
  const setUnsaved = useUnsaved((s) => s.setDirty);
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [dirty, setDirty] = useState(false);
  // UI-01 / UX3010-06: auto-numbered fields the user chose to type by hand
  const [manualAuto, setManualAuto] = useState<Set<string>>(() => new Set());
  // UX3010-02: the source data of a saved record is read first; «Изменить» opens its controls in place
  const [sourceEditing, setSourceEditing] = useState(false);
  const [advancedKeys, setAdvancedKeys] = useState<string[]>([]);
  // UX3010-05: what the last save was refused for, when it is not a field on screen (or not the input at all)
  const [formProblem, setFormProblem] = useState<FormProblem | null>(null);
  const touched = useRef(new Set<string>());
  // UX3010-04: the grid follows the width the form really has (page, drawer, phone, 200 % zoom)
  const box = useRef<HTMLDivElement>(null);
  const boxWidth = useElementWidth(box);
  const layout = formLayout(boxWidth ?? (screens.md ? 1000 : 360));

  const meta = useQuery({ queryKey: ["meta", entity], queryFn: () => api.get<EntityMeta>(`/${entity}/meta`) });
  const row = useQuery({
    queryKey: [entity, id],
    queryFn: () => api.get<Row>(`/${entity}/${id}`),
    enabled: !isNew,
  });
  const workflow = useQuery({
    queryKey: [entity, id, "workflow"],
    queryFn: () => api.get<Workflow>(`/${entity}/${id}/workflow`),
    enabled: !isNew && guided && !!meta.data?.workflow,
  });

  useEffect(() => {
    if (!meta.data) return;
    if (row.data) form.setFieldsValue(toFormValues(meta.data, row.data));
    if (isNew) {
      const values: Record<string, unknown> = {};
      // N10: the switch shows what will be saved — «Активно» is on when the server would store true
      for (const f of meta.data.fields) {
        if (f.type === "boolean" && typeof f.default === "boolean" && !f.readonly && !f.hidden && form.getFieldValue(f.name) === undefined) values[f.name] = f.default;
        // KZ 30.10.2026: «ТОО» chosen, «Казахстан, UTC+5 · Asia/Almaty» shown — the value a new form starts with
        if (f.initial !== null && f.initial !== undefined && !f.readonly && !f.hidden && form.getFieldValue(f.name) === undefined) values[f.name] = f.initial;
      }
      for (const f of meta.data.fields) {
        const value = prefill[f.name];
        if (value !== undefined) values[f.name] = f.type === "boolean" ? value === "true" : value;
      }
      form.setFieldsValue(values);
    }
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.data, row.data, form, isNew]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  // UX3010-13: the organization switcher in the header asks before it drops unsaved input. A drawer above the card
  // never touches the flag of the card under it; leaving the page clears it.
  useEffect(() => {
    if (!embedded) setUnsaved(dirty);
  }, [dirty, embedded, setUnsaved]);
  useEffect(
    () => () => {
      if (!embedded) setUnsaved(false);
    },
    [embedded, setUnsaved],
  );

  // section 6 of the test report: unsaved input is never lost silently
  useEffect(() => {
    if (!dirty || embedded) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, embedded]);

  // UX3010-11: a `static` value is set by the server (the working organization of a receipt): a line at the top of the
  // card, never a control, and never sent by the form
  const fields = useMemo(() => (meta.data ? editableFields(meta.data, isNew).filter((f) => !f.static) : []), [meta.data, isNew]);
  const staticFields = useMemo(() => (meta.data?.fields ?? []).filter((f) => f.static && !f.hidden), [meta.data]);
  const labels = useMemo(() => Object.fromEntries((meta.data?.fields ?? []).map((f) => [f.name, f.label])), [meta.data]);
  const refs = useMemo(() => Object.fromEntries((meta.data?.fields ?? []).map((f) => [f.name, f.ref])), [meta.data]);
  // BUG-R40-003: an approved count (and any status the server names) is not changed by a plain save
  const frozen = !isNew && !!meta.data?.frozen_statuses?.includes(String(row.data?.status ?? ""));
  const canWrite = !!meta.data && !meta.data.readonly && !frozen && can(`${meta.data.permission}.write`);
  // CW31-B08: per status, the fields a plain save may change (the lists of the server guard); the others are shown
  // read-only with the way to correct them, and the save sends only the changed allowed fields
  const editPolicy = meta.data?.edit_policy ?? null;
  const allowed = useMemo(() => (!isNew && row.data ? allowedFields(editPolicy, row.data.status) : null), [isNew, row.data, editPolicy]);
  const locked = (f: FieldMeta) => !!allowed && !allowed.has(f.name);
  const prefilled = new Set(isNew ? Object.keys(prefill).filter((k) => fields.some((f) => f.name === k)) : []);
  const { planHint, packageHint } = usePackagePlan(form, isNew && fields.some((f) => f.name === "packages_count") && fields.some((f) => f.name === "quantity_plan"));

  // ---- guided layout: context of the warehouse and the organization (section 6.3) -----------------------------
  const contextFields = useMemo(() => (guided ? fields.filter((f) => f.type === "ref" && f.context && f.ref) : []), [guided, fields]);
  const contextQueries = useQueries({
    queries: contextFields.map((f) => ({
      queryKey: ["ref", f.ref, "", f.context?.filter ?? null, ""],
      queryFn: () => api.get<Page<Record<string, unknown>>>(`/${f.ref}`, { size: 2, filter: f.context?.filter ?? undefined }),
      staleTime: 60_000,
    })),
  });
  // one candidate = the context; the control waits under «Дополнительно», the line above the form names the value
  const resolved: Record<string, Record<string, unknown> | null> = {};
  contextFields.forEach((f, i) => {
    const items = contextQueries[i]?.data?.items ?? [];
    resolved[f.name] = items.length === 1 ? items[0] : null;
  });
  const resolvedKey = JSON.stringify(Object.fromEntries(Object.entries(resolved).map(([k, v]) => [k, v?.id ?? null])));
  useEffect(() => {
    if (!isNew) return;
    for (const [name, candidate] of Object.entries(resolved)) {
      if (candidate && form.getFieldValue(name) === undefined && !touched.current.has(name)) form.setFieldValue(name, String(candidate.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedKey, isNew, form]);

  // ---- UX3010-10 / 13: a new contract or order starts with the working organization; a typed or prefilled value stays
  useEffect(() => {
    if (!isNew || !workingCompany) return;
    for (const f of fields) {
      if (f.working_default && prefill[f.name] === undefined && form.getFieldValue(f.name) === undefined && !touched.current.has(f.name)) {
        form.setFieldValue(f.name, workingCompany.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, workingCompany?.id, fields, form]);

  // ---- guided: the date of a new document is the day of the chosen warehouse (section 6.2) ------------------------
  const warehouseId = Form.useWatch("warehouse_id", form) as string | undefined;
  const dateField = guided && isNew ? fields.find((f) => f.name === "date" && f.type === "date") : undefined;
  const warehouse = useQuery({
    queryKey: ["warehouses", warehouseId],
    queryFn: () => api.get<Record<string, unknown>>(`/warehouses/${warehouseId}`),
    enabled: !!dateField && !!warehouseId,
    staleTime: 300_000,
  });
  const [dateOrigin, setDateOrigin] = useState<string | null>(null);
  useEffect(() => {
    if (!dateField || !warehouse.data || touched.current.has("date")) return;
    const { date, zone } = todayIn(warehouse.data.timezone as string | undefined);
    form.setFieldValue("date", dayjs(date));
    setDateOrigin(zone ? `Сегодня по складу «${String(warehouse.data.name ?? "")}» (${zone}); можно изменить` : "Сегодня по часам этого устройства; можно изменить");
  }, [dateField, warehouse.data, form]);

  // ---- guided: delivery modes hide the route fields they make pointless (section 10.2) ---------------------------
  const modeField = guided ? fields.find((f) => f.type === "ref" && f.mode && f.ref) : undefined;
  const modeRows = useRefRows(modeField?.ref ?? undefined);
  const modeValue = Form.useWatch(modeField?.name ?? "__mode__", form) as string | undefined;
  const modeRow = modeField && modeValue ? (modeRows.data?.items ?? []).find((r) => String(r.id) === modeValue) : undefined;
  const hidesRoute = !!modeField && !!modeRow && Boolean(modeRow[modeField.mode!.field]) === true;
  const hiddenByMode = new Set(hidesRoute ? modeField!.mode!.hides ?? [] : []);

  // BUG-007: a duplicate names the whole key and offers the record that already has it
  const [conflict, setConflict] = useState<{ detail: string; path: string | null } | null>(null);

  const showProblem = (e: unknown) => {
    if (e instanceof ApiError && Array.isArray(e.problem.fields) && e.problem.fields.length > 1) {
      const keyFields = e.problem.fields.filter((name) => fields.some((f) => f.name === name));
      form.setFields(keyFields.map((name) => ({ name, errors: ["Это сочетание уже используется другой записью"] })));
      setConflict({ detail: e.problem.detail ?? e.problem.title, path: e.problem.existing?.path ?? null });
      return;
    }
    if (guided) {
      showGuidedProblem(e);
      return;
    }
    const errors = fieldErrors(e);
    if (errors.length) {
      form.setFields(errors.map((x) => ({ name: x.field, errors: [x.message] })));
      form.scrollToField(errors[0].field, { behavior: "smooth", block: "center" });
      if (guardOn) setIssues(errors.filter((x) => fields.some((f) => f.name === x.field)).map((x) => ({ field: x.field, message: x.message })));
    }
    if (e instanceof ApiError && e.problem.existing?.path) {
      setConflict({ detail: e.problem.detail ?? e.problem.title, path: e.problem.existing.path });
    }
    message.error(describeError(e, labels), 6);
  };

  /**
   * UX3010-05: an error of input goes next to its field («что не так: как исправить»), opening «Дополнительно» or the
   * source data when the field lives there; a field the card does not show and the errors of lines are listed above
   * the buttons. A failure of the system is said as such, with its reference code, and no field turns red. The form
   * keeps everything typed in every case.
   */
  const showGuidedProblem = (e: unknown) => {
    const problem = explainProblem(e, labels);
    if (problem.kind !== "input") {
      setFormProblem(problem);
      message.error(problem.summary, 8);
      return;
    }
    const placed = problem.fields.filter((x) => placedNames.has(x.field));
    const unplaced = problem.fields.filter((x) => !placedNames.has(x.field));
    if (placed.some((x) => advancedNames.has(x.field))) setAdvancedKeys(["advanced"]);
    if (placed.some((x) => sourceNames.has(x.field))) setSourceEditing(true);
    if (guardOn) setIssues(placed);
    if (placed.length) {
      // UX1-025: an error on another step opens that step first, then the field
      openPartOf(placed[0].field);
      // after the folded block has opened and the source controls have mounted
      window.setTimeout(() => {
        form.setFields(placed.map((x) => ({ name: x.field, errors: [x.message] })));
        form.scrollToField(placed[0].field, { behavior: "smooth", block: "center" });
      }, 60);
    }
    setFormProblem(unplaced.length || problem.other.length ? { ...problem, fields: unplaced } : null);
    if (e instanceof ApiError && e.problem.existing?.path) {
      setConflict({ detail: e.problem.detail ?? e.problem.title, path: e.problem.existing.path });
    }
    message.error(problem.summary, 6);
  };

  // «Сохранить и добавить товары» (section 9): the header is saved by the ordinary service first; only its confirmed
  // id opens the lines with the parent set — the line is never created before the header
  const linesChild = useMemo(() => (meta.data?.children ?? []).find((c) => c.entity.endsWith("_lines") && c.fk), [meta.data]);
  const afterSave = useRef<"lines" | null>(null);
  const parentFk = useMemo(() => fields.find((f) => f.type === "ref" && prefilled.has(f.name) && f.name.endsWith("document_id")), [fields, prefilled]);

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const body = onlyChangedAllowed(fromFormValues(meta.data!, values, isNew ? null : row.data), isNew ? null : row.data, allowed);
      return isNew ? api.post<Row>(`/${entity}`, body) : api.patch<Row>(`/${entity}/${id}`, body);
    },
    onSuccess: async (saved) => {
      const name = String(saved.number ?? saved.code ?? saved.name ?? saved.username ?? "");
      const pendingBarcode = isNew && entity === "storage_objects" ? prefill.pending_barcode : undefined;
      if (pendingBarcode) {
        // section 5.2: the code that started «Создать новый товар» becomes the product's manufacturer code
        try {
          await api.post("/barcodes", { barcode: pendingBarcode, raw: pendingBarcode, storage_object_id: saved.id, source: "manufacturer" });
          message.success(`Штрихкод ${pendingBarcode} привязан к новому товару`);
        } catch (e) {
          message.error(`Товар создан, но код не привязан: ${describeError(e)}`, 8);
        }
      }
      setDirty(false);
      setFormProblem(null);
      setSourceEditing(false);
      queryClient.invalidateQueries({ queryKey: [entity] });
      queryClient.invalidateQueries({ queryKey: ["ref", entity] });
      if (embedded) {
        message.success(`Создано: ${name || rowLabel(saved, meta.data?.pick_label)}`);
        onSaved?.(saved);
        return;
      }
      if (!isNew) {
        message.success(`Сохранено${name ? `: ${name}` : ""}`);
        // the form shows what the server stored, not what was typed — a value it refused or rewrote is visible
        queryClient.setQueryData([entity, id], saved);
        if (meta.data) form.setFieldsValue(toFormValues(meta.data, saved));
        onSaved?.(saved);
        return;
      }
      const parentId = parentFk ? prefill[parentFk.name] : undefined;
      const parentEntity = parentFk?.ref;
      if (guided && parentFk && parentId && parentEntity) {
        // section 9: a saved line offers the next line or the document, with the context kept (AT-37)
        const key = `line-${saved.id}`;
        notification.success({
          key,
          message: `Товар добавлен${name ? `: ${name}` : ""}`,
          description: "Документ помнит склад и контрагента: добавьте следующий товар или вернитесь к документу.",
          duration: 0,
          btn: (
            <Space>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { notification.destroy(key); navigate(`/${entity}/new?${new URLSearchParams(prefill).toString()}`, { replace: true }); }}>
                Добавить ещё товар
              </Button>
              <Button size="small" onClick={() => { notification.destroy(key); navigate(`/${parentEntity}/${parentId}`); }}>
                К документу
              </Button>
            </Space>
          ),
        });
        navigate(`/${entity}/${saved.id}`, { replace: true });
        onSaved?.(saved);
        return;
      }
      if (afterSave.current === "lines" && linesChild) {
        afterSave.current = null;
        notification.success({ message: `Шапка сохранена${name ? `: ${name}` : ""}`, description: "Теперь добавьте товары: документ уже создан, строка добавится к нему.", duration: 6 });
        navigate(`/${linesChild.entity}/new?${new URLSearchParams({ [linesChild.fk]: saved.id }).toString()}`);
        onSaved?.(saved);
        return;
      }
      // UX 5.2: confirm what was created and say what to do next
      notification.success({
        message: `Создано${name ? `: ${name}` : ""}`,
        description: meta.data?.create_hint ?? undefined,
        duration: meta.data?.create_hint ? 10 : 4,
      });
      navigate(`/${entity}/${saved.id}`, { replace: true });
      onSaved?.(saved);
    },
    onError: showProblem,
  });

  // BUG-R42-001: a changed label code (the barcode of a cell) is saved only after agreeing to replace the label on the
  // shelf; «Отмена» puts the old code back into the field
  const submit = (values: Record<string, unknown>) => {
    setFormProblem(null);
    setIssues([]);
    const stored = row.data;
    const changed = isNew || !stored
      ? undefined
      : (meta.data?.fields ?? []).find((f) => {
          const before = String(stored[f.name] ?? "");
          const after = String(values[f.name] ?? "").trim();
          return !!f.change_warning && before !== "" && after !== "" && after !== before;
        });
    if (!changed) {
      save.mutate(values);
      return;
    }
    const before = String(stored![changed.name] ?? "");
    const after = String(values[changed.name] ?? "").trim();
    const code = String(stored!.code ?? stored!.number ?? "");
    modal.confirm({
      title: `Заменить ${changed.label.toLowerCase()}?`,
      content: changed.change_warning!.replace("{code}", code).replace("{old}", before).replace("{new}", after),
      okText: changed.name === "timezone" ? "Сменить пояс" : "Заменить код",
      cancelText: "Отмена",
      onOk: () => save.mutate(values),
      onCancel: () => form.setFieldValue(changed.name, before),
    });
  };

  const remove = useMutation({
    mutationFn: () => api.delete<void>(`/${entity}/${id}`),
    onSuccess: () => {
      message.success("Удалено");
      queryClient.invalidateQueries({ queryKey: [entity] });
      navigate(`/${entity}`);
    },
    onError: (e) => message.error(describeError(e, labels)),
  });

  const statusField = meta.data?.fields.find((f) => f.name === "status" && f.type === "enum");
  const statusLabel = statusField?.options?.find((o) => o.value === row.data?.status)?.label;

  // section 7.4: a row created from «+» is a saved record of its own; the document around it is not saved yet
  const onCreated = (created: Record<string, unknown>) => {
    if (isNew || dirty) message.info(`Запись создана: ${rowLabel(created)}. ${guided && linesChild ? "Документ" : "Текущая запись"} ещё не сохранён${guided && linesChild ? "" : "а"} — нажмите «Сохранить».`, 6);
  };

  /** UI-01 / UX3010-06: a new record's number the server assigns when left empty (`number`, or any `auto_number`). */
  const autoNumbered = (f: FieldMeta) => guided && isNew && f.type === "string" && !f.required && (!!f.auto_number || f.name === "number");

  const control = (f: FieldMeta) => {
    if (prefilled.has(f.name) && f.type === "ref") return <RefSelect entity={f.ref!} disabled />;
    if (f.name === "attributes" && f.type === "json") {
      return option("use_additional_attributes") ? <AttributesField entity={entity} /> : <Input.TextArea rows={3} style={{ fontFamily: "monospace" }} />;
    }
    if (f.name === "permissions" && f.type === "json") return <PermissionsSelect />;
    if (f.type === "ref" && f.ref_by) return <RefByFieldSelect field={f} form={form} labels={labels} />;
    if (guided && f.type === "ref" && f.mode && f.ref) return <ModeRefSelect field={f} />;
    if (f.type === "ref" && f.depends_on?.length) return <DependentRefSelect field={f} form={form} labels={labels} refs={refs} depth={depth} onCreated={onCreated} />;
    // UI-01 / UX3010-06: the number of a new document (any field the server numbers) is the server's; typing one by
    // hand stays an explicit choice
    if (autoNumbered(f) && !manualAuto.has(f.name)) {
      // the label already says «Номер»: the short placeholder fits the compact field, the full phrase is its title
      return <Input disabled placeholder="Автоматически" title="Номер присвоится автоматически при сохранении" data-testid={f.name === "number" ? "number-auto" : `${f.name}-auto`} />;
    }
    if (guardOn && f.type === "number" && f.unit_from) return <UnitNumberInput field={f} form={form} />;
    if (guardOn && f.type === "string" && f.check === "tax_id") return <TaxIdInput field={f} form={form} />;
    return <FieldInput field={f} depth={depth} onCreated={onCreated} guided={guided} saved={isNew ? undefined : row.data?.[f.name]} guard={guardOn} />;
  };

  const toggleManual = (name: string) => {
    const manual = manualAuto.has(name);
    // back to the automatic number: a number typed before is not sent
    if (manual) form.setFieldValue(name, undefined);
    setManualAuto((prev) => {
      const next = new Set(prev);
      if (manual) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const extraOf = (f: FieldMeta): React.ReactNode => {
    if (prefilled.has(f.name) && f.type === "ref") return "Задано карточкой, из которой вы пришли";
    if (autoNumbered(f)) {
      const manual = manualAuto.has(f.name);
      return (
        <span>
          {manual ? "Номер введён вручную; пустое поле вернёт автонумерацию. " : null}
          <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => toggleManual(f.name)} data-testid={f.name === "number" ? "number-manual" : `${f.name}-manual`}>
            {manual ? "Вернуть автонумерацию" : "Указать номер вручную"}
          </Button>
        </span>
      );
    }
    if (f.name === "date" && dateOrigin && !touched.current.has("date")) return dateOrigin;
    if (f.name === "quantity_plan" && planHint) return planHint;
    if (f.name === "package_id" && packageHint) return packageHint;
    if (f.unit_from) return <UnitHint form={form} field={f} />;
    if (f.notice_on_change) {
      return (
        <>
          {f.description ?? null}
          <SwitchNotice field={f} form={form} saved={isNew ? undefined : row.data?.[f.name]} />
        </>
      );
    }
    return f.description ?? undefined;
  };

  const renderField = (f: FieldMeta) => {
    if (hiddenByMode.has(f.name)) {
      const value = form.getFieldValue(f.name);
      if (value === undefined || value === null || value === "") return null;
      // section 10.2: a saved address survives a switch to pickup until the user clears it on purpose
      return (
        <Col key={f.name} xs={24}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`При самовывозе «${f.label}» не нужен`}
            description={<span>Значение останется в документе, пока вы его не очистите: <RefLabel entity={f.ref!} id={String(value)} /></span>}
            action={<Button size="small" onClick={() => { form.setFieldValue(f.name, undefined); setDirty(true); }}>Очистить</Button>}
          />
          <Form.Item name={f.name} hidden>
            <Input />
          </Form.Item>
        </Col>
      );
    }
    // UX3010-04: with the guided layout the column follows the width of the form itself (see formLayout.ts)
    const colProps = guided ? { span: columnSpan(f.width, layout) } : { xs: 24, md: f.type === "json" || (f.max_length ?? 0) > 300 ? 24 : 12 };
    // CW31-B08: a requisite the server keeps fixed in this status is read here, never offered for a plain save
    const fixed = locked(f);
    const item = (
      <Form.Item
        name={f.name}
        label={
          f.label_by ? (
            <DynamicLabel field={f} form={form} />
          ) : (
            <span>
              {f.label}
              {f.help && <HelpHint subject={f.label} text={f.help} size={13} field />}
            </span>
          )
        }
        valuePropName={f.type === "boolean" ? "checked" : "value"}
        dependencies={f.label_by ? [f.label_by.field] : undefined}
        rules={[
          // KZ 30.10.2026 (ТЗ реквизитов §5.3): a new card from the form has its identifier («БИН» / «ИИН»)
          ...(f.required_new && isNew && !f.required
            ? [{ required: true, whitespace: true, message: `Обязательное поле: заполните ${labelFor(f, form.getFieldValue(f.label_by?.field ?? "__none__"))}` }]
            : []),
          // the quick check of the requisite (БИН / ИИН, ИИК, БИК, КБе); the server applies the same policy
          ...(f.check
            ? [
                {
                  validator: (_: unknown, value: unknown) => {
                    const saved = isNew ? undefined : row.data?.[f.name];
                    // an old stored value sent back unchanged is not blocked, as on the server
                    if (saved !== undefined && saved !== null && String(saved) === String(value ?? "")) return Promise.resolve();
                    const problem = checkProblem(f.check, value, form.getFieldValue(f.label_by?.field ?? "__none__"));
                    return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
                  },
                },
              ]
            : []),
          // BUG-002: a value of spaces only is empty — refused here as on the server
          ...(f.required || (f.type === "password" && isNew)
            ? guided && f.type === "string"
              ? [
                  // UX3010-05: empty and «only spaces» are told apart, as the server does
                  { required: true, message: "Обязательное поле: заполните его" },
                  {
                    validator: (_: unknown, value: unknown) =>
                      typeof value === "string" && value !== "" && value.trim() === ""
                        ? Promise.reject(new Error("Обязательное поле: одни пробелы не считаются значением — введите текст"))
                        : Promise.resolve(),
                  },
                ]
              : [
                  // only text fields get `whitespace`: the key alone makes the validator treat the value as a string,
                  // and a number field would then fail as «empty» (found by the browser check of this fix)
                  f.type === "string" || f.type === "password"
                    ? { required: true, whitespace: true, message: "Обязательное поле: заполните его" }
                    : { required: true, message: "Обязательное поле: заполните его" },
                ]
            : []),
          // UX3010-05: a too long text stays whole in the field and the rule says by how much it is too long
          ...((guided || guardOn) && f.type === "string" && f.max_length
            ? [
                {
                  validator: (_: unknown, value: unknown) => {
                    const problem = tooLong(value, f.max_length);
                    return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
                  },
                },
              ]
            : []),
          ...numberRules(f),
          // UXW-63: «1,» is a step on the way, not a value — the final check on save asks to finish it
          ...(guardOn && f.type === "number"
            ? [{ validator: (_: unknown, value: unknown) => (unfinishedNumber(value) ? Promise.reject(new Error(unfinishedNumber(value)!)) : Promise.resolve()) }]
            : []),
        ].filter(() => !fixed)}
        extra={extraOf(f)}
      >
        {control(f)}
      </Form.Item>
    );
    return (
      <Col key={f.name} {...colProps} data-testid={`field-${f.name}`} data-locked={fixed || undefined}>
        {fixed ? <ConfigProvider componentDisabled>{item}</ConfigProvider> : item}
      </Col>
    );
  };

  // ---- blocks of the card: main, groups, advanced (with the source data inside) -----------------------------------
  const groups = guided ? meta.data?.groups ?? [] : [];
  const sourceGroups = new Set(groups.filter((g) => g.source).map((g) => g.key));
  const sourceGroup = groups.find((g) => g.source);
  // UX3010-02: the number and date of the source document and the ERP id of the exchange are integration data —
  // inside the folded «Дополнительно» on a new and on a saved record alike, never in the main grid
  const isSource = (f: FieldMeta) => !!sourceGroup && ((!!f.group && sourceGroups.has(f.group)) || f.name === "external_id");
  const isAdvanced = (f: FieldMeta) => f.advanced || isSource(f) || (guided && !!f.context && !!resolved[f.name]);
  const inGroup = (f: FieldMeta) => (guided && f.group && groups.some((g) => g.key === f.group && !g.source) ? f.group : null);
  const mainFields = fields.filter((f) => !isAdvanced(f) && !inGroup(f));
  // ---- stepwise: which part of the input a block belongs to (the parts are those of stepwise.ts) -----------------
  const parts = formParts(meta.data, guided);
  const lastPart = parts[parts.length - 1]?.key ?? MAIN_PART;
  const partOf = (f: FieldMeta): string => {
    const g = inGroup(f);
    if (g && !groups.find((x) => x.key === g)?.collapsed) return g;
    if (isAdvanced(f)) return lastPart;
    return MAIN_PART;
  };
  const showPart = (key: string) => !stepwise || stepwise.part === null || stepwise.part === key;
  // display:none, not unmount: the fields of a hidden part keep their registration and values (UX1-011/012)
  const partStyle = (key: string): React.CSSProperties | undefined => (showPart(key) ? undefined : { display: "none" });
  const openPartOf = (name: string) => {
    const field = fields.find((f) => f.name === name);
    if (stepwise && field && !showPart(partOf(field))) stepwise.onPartNeeded(partOf(field));
  };
  /** UXW-57 / ER01: a link of the summary opens the step, «Дополнительно» or the source data and puts the caret in. */
  const goToField = (name: string) => {
    openPartOf(name);
    const field = fields.find((f) => f.name === name);
    if (field && isAdvanced(field)) setAdvancedKeys(["advanced"]);
    if (field && isSource(field)) setSourceEditing(true);
    window.setTimeout(() => form.scrollToField(name, { behavior: "smooth", block: "center", focus: true }), 80);
  };
  const advancedFields = fields.filter((f) => isAdvanced(f));
  const sourceEditFields = advancedFields.filter(isSource);
  const otherAdvanced = advancedFields.filter((f) => !isSource(f));
  const sourceViewFields = (meta.data?.fields ?? []).filter((f) => !f.hidden && isSource(f));
  // a saved record shows what the source sent («Не получено» when nothing); the controls open on «Изменить»
  const sourceView = !isNew && !!row.data && !sourceEditing && sourceViewFields.length > 0;
  const placedNames = new Set(fields.filter((f) => !(sourceView && isSource(f))).map((f) => f.name));
  const advancedNames = new Set(advancedFields.map((f) => f.name));
  const sourceNames = new Set(sourceEditFields.map((f) => f.name));
  const sourceValue = (f: FieldMeta): string => {
    const value = row.data?.[f.name];
    if (value === null || value === undefined || value === "") return "Не получено";
    if (f.type === "date") return formatDate(value) || String(value);
    if (f.type === "datetime") return formatDateTime(value) || String(value);
    return String(value);
  };

  const sourceBlock = sourceGroup && sourceViewFields.length > 0 ? (
    <div data-testid="group-source" style={{ marginBottom: 12 }}>
      <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
        {sourceGroup.label}
        {sourceGroup.help && <HelpHint subject={sourceGroup.label} text={sourceGroup.help} size={13} />}
      </Typography.Title>
      {sourceView ? (
        <>
          <Descriptions size="small" column={layout === "wide" ? 3 : 1} bordered layout={layout === "phone" ? "vertical" : "horizontal"}>
            {sourceViewFields.map((f) => (
              <Descriptions.Item key={f.name} label={f.label}>
                <span data-testid={`source-${f.name}`}>{sourceValue(f)}</span>
              </Descriptions.Item>
            ))}
          </Descriptions>
          {canWrite && sourceEditFields.length > 0 && (
            <Button type="link" size="small" style={{ paddingInline: 0, marginTop: 4 }} data-testid="source-edit" onClick={() => setSourceEditing(true)}>
              Изменить данные источника
            </Button>
          )}
        </>
      ) : (
        <Row gutter={16}>{sourceEditFields.map(renderField)}</Row>
      )}
    </div>
  ) : null;

  /** UX3010-11: «Организация: ТОО Альфа» — the value the server sets, at the top of the card. */
  const staticValue = (f: FieldMeta): React.ReactNode => {
    if (!isNew) {
      const value = row.data?.[f.name];
      if (value === null || value === undefined || value === "") return "не указана";
      return f.ref ? <RefLabel entity={f.ref} id={String(value)} /> : String(value);
    }
    if (f.ref === "companies") return workingCompany?.name ?? "организация по умолчанию";
    return "определится при сохранении";
  };
  const staticLine = staticFields.length > 0 && (isNew || !!row.data) && (
    <Typography.Paragraph data-testid="static-line" style={{ marginBottom: 12 }}>
      {staticFields.map((f, i) => (
        <span key={f.name} data-testid={`static-${f.name}`}>
          {i > 0 ? " · " : ""}
          <Typography.Text type="secondary">{f.label}: </Typography.Text>
          <Typography.Text strong>{staticValue(f)}</Typography.Text>
          {f.help && <HelpHint subject={f.label} text={f.help} size={13} field />}
        </span>
      ))}
    </Typography.Paragraph>
  );

  // the summary follows the form: a changed field leaves the list at once (its error is cleared on change too)
  const liveIssues = guardOn ? issues : [];
  const issuesBlock = liveIssues.length > 0 && (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      data-testid="form-issues"
      message={checkWords(liveIssues.length)}
      description={
        <>
          <div>Введённое сохранено. Нажмите на название поля, чтобы перейти к нему.</div>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {liveIssues.map((x) => (
              <li key={x.field}>
                <Button type="link" size="small" style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }} onClick={() => goToField(x.field)} data-testid={`issue-${x.field}`}>
                  {labelOf(x.field, labels)}
                </Button>
                : {x.message}
              </li>
            ))}
          </ul>
        </>
      }
    />
  );

  const problemBlock = formProblem && (
    <Alert
      type={formProblem.kind === "input" || formProblem.kind === "conflict" ? "warning" : "error"}
      showIcon
      closable
      onClose={() => setFormProblem(null)}
      style={{ marginBottom: 12 }}
      data-testid="form-problem"
      data-kind={formProblem.kind}
      message={
        formProblem.kind === "input"
          ? "Не сохранено: исправьте значения"
          : formProblem.kind === "network"
            ? "Нет связи с сервером — данные остались в форме"
            : formProblem.kind === "denied"
              ? "Недостаточно прав"
              : // ER02: an ended session, a record that is gone and a record changed by someone else are said as such
                formProblem.kind === "auth"
                ? "Сеанс завершён"
                : formProblem.kind === "not_found"
                  ? "Запись не найдена"
                  : formProblem.kind === "conflict"
                    ? "Данные изменились, пока вы работали"
                    : "Ошибка системы — это не ошибка в заполнении формы"
      }
      description={
        formProblem.kind === "input" ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {formProblem.fields.map((x) => (
              <li key={x.field}>{x.message.startsWith(`«${labelOf(x.field, labels)}»`) ? x.message : `«${labelOf(x.field, labels)}»: ${x.message}`}</li>
            ))}
            {formProblem.other.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ) : (
          <span>
            {formProblem.summary}
            {formProblem.reference && (
              <>
                {" "}Код обращения: <Typography.Text copyable code data-testid="form-problem-reference">{formProblem.reference}</Typography.Text>
              </>
            )}
          </span>
        )
      }
    />
  );

  const contextLine = guided && (fields.some((f) => f.name === "warehouse_id") || contextFields.length > 0) && (
    <Typography.Paragraph type="secondary" data-testid="context-line" style={{ marginBottom: 12 }}>
      {fields.some((f) => f.name === "warehouse_id") && (
        <span>
          Склад: {warehouseId ? <RefLabel entity="warehouses" id={warehouseId} /> : "не выбран"}
        </span>
      )}
      {contextFields.map((f) => (resolved[f.name] ? <ContextItem key={f.name} field={f} form={form} candidate={resolved[f.name]!} /> : null))}
    </Typography.Paragraph>
  );

  const mainForm = (
    <Form
      form={form}
      layout="vertical"
      disabled={!canWrite}
      onFinish={submit}
      onFinishFailed={({ errorFields }) => {
        if (guardOn) setIssues(errorFields.filter((x) => typeof x.name[0] === "string").map((x) => ({ field: String(x.name[0]), message: x.errors[0] ?? "" })));
        // UX1-025: a required field of a hidden part — open its step and show the field, nothing typed is lost
        const name = errorFields[0]?.name?.[0];
        if (typeof name === "string") {
          openPartOf(name);
          window.setTimeout(() => form.scrollToField(name, { behavior: "smooth", block: "center" }), 60);
        }
      }}
      onKeyDown={(e) => {
        // BUG-003 / UI-07: Enter does the job of the field it is pressed in (confirms a date, picks an option,
        // moves on) and never saves the whole document — saving is the «Сохранить» button. The browser submits a form
        // on Enter in any text input («implicit submission»); only preventDefault stops that, stopPropagation did not.
        const target = e.target as HTMLElement;
        if (e.key === "Enter" && target.tagName === "INPUT") e.preventDefault();
      }}
      onValuesChange={(changed) => {
        setConflict(null);
        setDirty(true);
        if (issues.length) setIssues((prev) => prev.filter((x) => !(x.field in changed)));
        for (const name of Object.keys(changed)) touched.current.add(name);
        form.setFields(Object.keys(changed).map((name) => ({ name, errors: [] })));
      }}
    >
      {staticLine}
      {issuesBlock}
      {!stepwise && !isNew && guided && workflow.data && <WorkflowPanel workflow={workflow.data} onOpenTab={onOpenTab} hideNext={panelOn && !!meta.data?.actions.length} />}
      {!stepwise && !isNew && meta.data && meta.data.actions.length > 0 && panelOn && (
        // UXW-47…53: the same actions in groups; like the row below, outside the disabled state of a read-only form
        // (UXW-47: a frozen card does not disable a separately allowed action dialog)
        <ConfigProvider componentDisabled={false}>
          <ActionPanel
            entity={entity}
            id={id}
            meta={meta.data}
            row={row.data as Record<string, unknown> | undefined}
            workflow={guided ? workflow.data ?? null : null}
            view="full"
            onOpenTab={onOpenTab}
            onDone={() => {
              queryClient.invalidateQueries({ queryKey: [entity, id] });
              queryClient.invalidateQueries({ queryKey: [entity, id, "workflow"] });
              void refreshChildLists(queryClient, entity, id, meta.data?.children);
            }}
          />
        </ConfigProvider>
      )}
      {!stepwise && !isNew && meta.data && meta.data.actions.length > 0 && !panelOn && (
        <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }} wrap>
          <Typography.Text strong>{statusLabel ? `Статус: ${statusLabel}` : ""}</Typography.Text>
          {/* the client flow of 30.10.2026: the actions sit inside the form, which is disabled on a read-only or frozen
              card or without the right to edit — the confirm «Да» and the inputs of the action dialogs inherited that
              and could not be used; the actions keep their own rights and reasons, the server checks every one */}
          <ConfigProvider componentDisabled={false}>
          <Space wrap>
            <DocumentActions
              entity={entity}
              id={id}
              status={String(row.data?.status ?? "")}
              actions={meta.data.actions}
              row={row.data as Record<string, unknown>}
              permission={meta.data.permission}
              workflow={guided ? workflow.data ?? null : null}
              onDone={() => {
                queryClient.invalidateQueries({ queryKey: [entity, id] });
                queryClient.invalidateQueries({ queryKey: [entity, id, "workflow"] });
                // CW31-B11: the child tabs of the record («Оплаты») follow the action at once
                void refreshChildLists(queryClient, entity, id, meta.data?.children);
              }}
            />
            <AssistantDocumentButton entity={entity} number={row.data?.number} />
          </Space>
          </ConfigProvider>
        </Space>
      )}
      {isNew && !canWrite && meta.data && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="У вашей роли нет права создавать записи в этом разделе"
          description="Форма открыта только для просмотра. Обратитесь к руководителю или администратору."
        />
      )}
      {isNew && canWrite && meta.data?.create_hint && !embedded && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={meta.data.create_hint} />}
      {frozen && meta.data?.frozen_hint && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={meta.data.frozen_hint} />}
      {editPolicy && allowed && fields.some(locked) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="edit-policy"
          message={editPolicy.hint}
          description={fixFor(editPolicy, row.data?.status) ?? undefined}
        />
      )}
      {conflict && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setConflict(null)}
          style={{ marginBottom: 16 }}
          message="Такая запись уже есть"
          description={conflict.detail}
          action={conflict.path ? <Link to={conflict.path}>Открыть существующую</Link> : undefined}
        />
      )}
      <div data-testid="part-main" style={partStyle(MAIN_PART)}>
        {contextLine}
        <Row gutter={16}>{mainFields.map(renderField)}</Row>
      </div>
      {groups
        .filter((g) => !g.source && fields.some((f) => f.group === g.key))
        .map((g) => {
          const groupFields = fields.filter((f) => f.group === g.key);
          const body = <Row gutter={16}>{groupFields.map(renderField)}</Row>;
          // a folded group («Допуски количества») belongs to the main part of the input
          if (g.collapsed) {
            return (
              <div key={g.key} style={partStyle(MAIN_PART)}>
                <CollapsedGroup group={g} fields={groupFields} form={form}>{body}</CollapsedGroup>
              </div>
            );
          }
          return (
            <div key={g.key} data-testid={`group-${g.key}`} style={{ marginBottom: 8, ...partStyle(g.key) }}>
              <Typography.Title level={5} style={{ marginTop: 4, marginBottom: 12 }}>
                {g.label}
                {g.help && <HelpHint subject={g.label} text={g.help} size={13} />}
              </Typography.Title>
              {body}
            </div>
          );
        })}
      <div data-testid="part-last" style={partStyle(lastPart)}>
      {(advancedFields.length > 0 || !!sourceBlock) && (
        <Collapse
          ghost
          style={{ marginBottom: 12 }}
          data-testid="advanced"
          // guided: an error inside opens the block (UX3010-05); without the option it opens only by hand, as before
          {...(guided ? { activeKey: advancedKeys, onChange: (keys: string | string[]) => setAdvancedKeys(Array.isArray(keys) ? keys : [keys]) } : {})}
          items={[
            {
              key: "advanced",
              // the folded fields stay registered: a context value (the organization) is sent with the save
              forceRender: true,
              label: sourceBlock ? "Дополнительно: данные источника и технические параметры" : "Дополнительно (технические параметры, обычно не нужны)",
              children: sourceBlock ? (
                <>
                  {sourceBlock}
                  {otherAdvanced.length > 0 && <Row gutter={16}>{otherAdvanced.map(renderField)}</Row>}
                </>
              ) : (
                <Row gutter={16}>{advancedFields.map(renderField)}</Row>
              ),
            },
          ]}
        />
      )}
      {canWrite && meta.data?.form_preview && <FormPreview entity={entity} id={isNew ? undefined : id} form={form} />}
      </div>
      {problemBlock}
      {canWrite && (
        <Space wrap>
          <Button type={!stepwise || stepwise.last ? "primary" : "default"} htmlType="submit" icon={<SaveOutlined />} loading={save.isPending} data-testid="save">
            Сохранить
          </Button>
          {guided && isNew && !embedded && linesChild && (
            <Button icon={<PlusOutlined />} loading={save.isPending} data-testid="save-and-lines" onClick={() => { afterSave.current = "lines"; form.submit(); }}>
              Сохранить и добавить товары
            </Button>
          )}
          {liveIssues.length > 0 && (
            <Button onClick={() => goToField(liveIssues[0].field)} data-testid="issue-first">
              К первой ошибке
            </Button>
          )}
          {embedded && (
            <Button icon={<CloseOutlined />} onClick={onCancel} data-testid="cancel">
              Отмена
            </Button>
          )}
          {!isNew && !embedded && (
            <Popconfirm title="Удалить запись?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove.mutate()}>
              <Button danger icon={<DeleteOutlined />} loading={remove.isPending}>
                Удалить
              </Button>
            </Popconfirm>
          )}
          {dirty ? <Typography.Text type="warning">Есть несохранённые изменения</Typography.Text> : !isNew && row.data ? <Typography.Text type="secondary">Сохранено</Typography.Text> : null}
        </Space>
      )}
      {!isNew && row.data && auditLine(row.data) && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          {auditLine(row.data)}
        </Typography.Paragraph>
      )}
    </Form>
  );

  return <div ref={box}>{mainForm}</div>;
}
